import React from 'react'
import { observer, inject } from 'mobx-react'
import i18next from 'i18next'
import { selectionTypes } from '../../constants'
import GlobalModal from './GlobalModal'

const SaveOrUpgradeModal = ({ uiStore, selectionStore, dataStore }) => {
  const {
    saveModalIsOpen,
    saveModalType,
    saveModalTitle,
    saveModalSelectedOption,
    saveModalSelectedGroup,
    saveModalTargetGroupName,
    saveModalFolderOption,
    saveModalSelectedFolder,
    saveModalNewFolderName,
    saveModalUpgradeDescription,
    saveModalConfirmText,
    saveModalCancelText,
    saveModalShowCancel,
    saveModalShowConfirm,
    saveModalAvailableGroups,
    saveModalAvailableFolders,
  } = uiStore

  const confirmDisabled = (() => {
    if (saveModalType === 'saveToFolder') {
      if (saveModalFolderOption === 'new') {
        return !saveModalNewFolderName || !saveModalNewFolderName.trim()
      }
      return !saveModalSelectedFolder
    }
    return false
  })()

  // Get the currently selected target group name
  const getCurrentTargetGroupName = () => {
    if (!selectionStore) return null
    const isTargetGroupSelected = selectionStore.type === selectionTypes.TARGET_GROUP
    let targetGroup = null
    if (isTargetGroupSelected) {
      targetGroup = selectionStore.item
    } else {
      targetGroup = selectionStore.data && selectionStore.data.group
    }
    return targetGroup && targetGroup.name ? targetGroup.name.trim() : null
  }

  const currentTargetGroupName = getCurrentTargetGroupName()

  // Filter out the currently selected target group from available options
  const availableGroups = currentTargetGroupName
    ? saveModalAvailableGroups.filter(group => group !== currentTargetGroupName)
    : saveModalAvailableGroups

  const availableFolders = currentTargetGroupName
    ? saveModalAvailableFolders.filter(folder => folder !== currentTargetGroupName)
    : saveModalAvailableFolders

  const renderModalContent = () => {
    switch (saveModalType) {
      case 'saveChanges':
        return (
          <div className='modal-form'>
            <div className='unified-radio-group'>
              <label className='unified-radio-option'>
                <input
                  type='radio'
                  name='targetGroup'
                  value='existing'
                  checked={saveModalSelectedOption === 'existing'}
                  onChange={() => uiStore.saveModalSetSelectedOption('existing')}
                />
                <span className='unified-radio-label'>
                  {i18next.t('MODAL.TARGET_GROUP_EXISTING')}
                </span>
              </label>
              <label className='unified-radio-option'>
                <input
                  type='radio'
                  name='targetGroup'
                  value='new'
                  checked={saveModalSelectedOption === 'new'}
                  onChange={() => uiStore.saveModalSetSelectedOption('new')}
                />
                <span className='unified-radio-label'>
                  {i18next.t('MODAL.TARGET_GROUP_NEW')}
                </span>
              </label>
            </div>

            {saveModalSelectedOption === 'existing' && (
              <div className='input-group'>
                <select
                  value={saveModalSelectedGroup}
                  onChange={(e) => uiStore.saveModalSetSelectedGroup(e.target.value)}
                  className='modal-select'
                >
                  <option value=''>{i18next.t('MODAL.TARGET_GROUP_SELECT')}</option>
                  {availableGroups.map((group) => (
                    <option key={group} value={group}>
                      {group}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {saveModalSelectedOption === 'new' && (
              <div className='input-group'>
                <input
                  type='text'
                  placeholder={i18next.t('MODAL.TARGET_GROUP_NAME_PLACEHOLDER')}
                  value={saveModalTargetGroupName}
                  onChange={(e) => uiStore.saveModalSetTargetGroupName(e.target.value)}
                  className='modal-input'
                />
              </div>
            )}
          </div>
        )

      case 'saveToFolder':
        return (
          <div className='modal-form'>
            <div className='unified-radio-group'>
              <label className='unified-radio-option'>
                <input
                  type='radio'
                  name='folderOption'
                  value='existing'
                  checked={saveModalFolderOption === 'existing'}
                  onChange={() => uiStore.saveModalSetFolderOption('existing')}
                />
                <span className='unified-radio-label'>
                  {i18next.t('MODAL.FOLDER_EXISTING')}
                </span>
              </label>
              <label className='unified-radio-option'>
                <input
                  type='radio'
                  name='folderOption'
                  value='new'
                  checked={saveModalFolderOption === 'new'}
                  onChange={() => uiStore.saveModalSetFolderOption('new')}
                />
                <span className='unified-radio-label'>
                  {i18next.t('MODAL.FOLDER_NEW')}
                </span>
              </label>
            </div>

            {saveModalFolderOption === 'existing' && (
              <div className='input-group'>
                <select
                  value={saveModalSelectedFolder}
                  onChange={(e) => uiStore.saveModalSetSelectedFolder(e.target.value)}
                  className='modal-select'
                >
                  <option value=''>{i18next.t('MODAL.FOLDER_SELECT')}</option>
                  {availableFolders.map((folder) => (
                    <option key={folder} value={folder}>
                      {folder}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {saveModalFolderOption === 'new' && (
              <div className='input-group'>
                <input
                  type='text'
                  placeholder={i18next.t('MODAL.FOLDER_NAME_PLACEHOLDER')}
                  value={saveModalNewFolderName}
                  onChange={(e) => uiStore.saveModalSetNewFolderName(e.target.value)}
                  className='modal-input'
                />
              </div>
            )}
          </div>
        )

      case 'upgrade':
        return (
          <div className='upgrade-modal-content'>
            <p className='upgrade-description'>{saveModalUpgradeDescription}</p>
          </div>
        )

      default:
        return uiStore.saveModalContent
    }
  }

  const handleCancelClick = () => {
    if (saveModalType === 'upgrade') {
      uiStore.saveModalHandleLearnMore()
    } else {
      uiStore.saveModalHandleCancel()
    }
  }

  return (
    <GlobalModal
      isOpen={saveModalIsOpen}
      title={saveModalTitle}
      onClose={() => uiStore.saveModalCloseModal()}
      showConfirm={saveModalShowConfirm}
      confirmText={saveModalConfirmText}
      onConfirm={() => uiStore.saveModalHandleConfirm()}
      showCancel={saveModalShowCancel}
      cancelText={saveModalCancelText}
      onCancel={handleCancelClick}
      showProBadge={saveModalType === 'upgrade'}
      isUpgradeModal={saveModalType === 'upgrade'}
      confirmDisabled={confirmDisabled}
    >
      {renderModalContent()}
    </GlobalModal>
  )
}

export default inject('uiStore', 'selectionStore', 'dataStore')(observer(SaveOrUpgradeModal))
